import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, Stack, TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import Modal from '../../Components/Modals/Modal';
import {
	createJobAssignment,
	fetchAssignableUsers,
	updateJobAssignment,
} from '../../Shared/Api';
import { fetchAllJobsList } from '../../Shared/jobs';

const emptyForm = {
	job: null,
	user: null,
	starts_at: dayjs().hour(9).minute(0).second(0),
	ends_at: dayjs().hour(13).minute(0).second(0),
	notes: '',
};

const AssignmentModal = ({
	open,
	onClose,
	assignment = null,
	onSaved,
	fixedJob = null,
	defaults = null,
	onDelete,
}) => {
	const { enqueueSnackbar } = useSnackbar();
	const [loading, setLoading] = useState(false);
	const [jobs, setJobs] = useState([]);
	const [users, setUsers] = useState([]);
	const [form, setForm] = useState(emptyForm);

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;
		const load = async () => {
			try {
				const [jobList, userList] = await Promise.all([
					fixedJob ? Promise.resolve([fixedJob]) : fetchAllJobsList(),
					fetchAssignableUsers(),
				]);
				if (cancelled) return;
				setJobs(jobList || []);
				setUsers(userList || []);

				if (assignment) {
					const jobMatch = (jobList || []).find(
						(j) => j.id === assignment.job,
					) ||
						(fixedJob && fixedJob.id === assignment.job ? fixedJob : null) || {
							id: assignment.job,
							name: assignment.job_name,
						};
					const userMatch = (userList || []).find(
						(u) => u.id === assignment.user,
					) || {
						id: assignment.user,
						display_name: assignment.user_name,
						email: assignment.user_email,
					};
					setForm({
						job: jobMatch,
						user: userMatch,
						starts_at: dayjs(assignment.starts_at),
						ends_at: dayjs(assignment.ends_at),
						notes: assignment.notes || '',
					});
				} else {
					const prefUser =
						defaults?.user &&
						((userList || []).find((u) => u.id === defaults.user.id) ||
							defaults.user);
					const prefJob =
						defaults?.job &&
						((jobList || []).find((j) => j.id === defaults.job.id) ||
							defaults.job ||
							fixedJob);
					setForm({
						...emptyForm,
						job: prefJob || fixedJob || null,
						user: prefUser || null,
						starts_at: defaults?.starts_at
							? dayjs(defaults.starts_at)
							: dayjs().hour(9).minute(0).second(0),
						ends_at: defaults?.ends_at
							? dayjs(defaults.ends_at)
							: dayjs().hour(13).minute(0).second(0),
						notes: '',
					});
				}
			} catch (error) {
				enqueueSnackbar(
					error.message || 'Failed to load assignment form data.',
					{ variant: 'error' },
				);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [open, assignment, fixedJob, defaults, enqueueSnackbar]);

	const handleSave = async () => {
		if (!form.job?.id || !form.user?.id || !form.starts_at || !form.ends_at) {
			enqueueSnackbar('Who, job, start, and end are required.', {
				variant: 'warning',
			});
			return;
		}
		if (!form.ends_at.isAfter(form.starts_at)) {
			enqueueSnackbar('End time must be after start time.', {
				variant: 'warning',
			});
			return;
		}

		setLoading(true);
		const payload = {
			job: form.job.id,
			user: form.user.id,
			starts_at: form.starts_at.toISOString(),
			ends_at: form.ends_at.toISOString(),
			notes: form.notes || '',
		};

		try {
			if (assignment?.id) {
				await updateJobAssignment(assignment.id, payload);
				enqueueSnackbar('Assignment updated.', { variant: 'success' });
			} else {
				await createJobAssignment(payload);
				enqueueSnackbar('Assignment created.', { variant: 'success' });
			}
			onSaved?.();
			onClose();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to save assignment.', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle={assignment ? 'Edit assignment' : 'New assignment'}
			maxWidth="sm"
			alwaysMaxWidth
			useActionButtons
			showCancelButton
			actionButtons={
				<Stack direction="row" spacing={1}>
					{assignment && onDelete ? (
						<Button
							color="error"
							variant="outlined"
							onClick={onDelete}
							disabled={loading}
						>
							Delete
						</Button>
					) : null}
					<Button variant="contained" onClick={handleSave} disabled={loading}>
						{loading ? 'Saving...' : 'Save'}
					</Button>
				</Stack>
			}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<Autocomplete
						options={users}
						value={form.user}
						onChange={(_, value) =>
							setForm((prev) => ({ ...prev, user: value }))
						}
						getOptionLabel={(option) =>
							option?.display_name ||
							[option?.first_name, option?.last_name]
								.filter(Boolean)
								.join(' ') ||
							option?.username ||
							''
						}
						isOptionEqualToValue={(a, b) => a?.id === b?.id}
						renderInput={(params) => (
							<TextField {...params} label="Who" required />
						)}
					/>
					<Autocomplete
						options={jobs}
						value={form.job}
						disabled={Boolean(fixedJob)}
						onChange={(_, value) =>
							setForm((prev) => ({ ...prev, job: value }))
						}
						getOptionLabel={(option) =>
							option?.name
								? option.customer_name
									? `${option.name} (${option.customer_name})`
									: option.name
								: ''
						}
						isOptionEqualToValue={(a, b) => a?.id === b?.id}
						renderInput={(params) => (
							<TextField {...params} label="Job" required />
						)}
					/>
					<DateTimePicker
						label="Starts"
						value={form.starts_at}
						onChange={(value) =>
							setForm((prev) => ({ ...prev, starts_at: value }))
						}
						slotProps={{ textField: { fullWidth: true, required: true } }}
					/>
					<DateTimePicker
						label="Ends"
						value={form.ends_at}
						onChange={(value) =>
							setForm((prev) => ({ ...prev, ends_at: value }))
						}
						slotProps={{ textField: { fullWidth: true, required: true } }}
					/>
					<TextField
						label="Notes"
						value={form.notes}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, notes: e.target.value }))
						}
						multiline
						minRows={2}
						fullWidth
					/>
				</Stack>
			</LocalizationProvider>
		</Modal>
	);
};

export default AssignmentModal;
